import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useChartTimeWindowStore } from '../../Store/chartTimeWindowStore';
import {
  DEFAULT_VISIBLE_SECONDS,
  MIN_VISIBLE_BARS,
  RESOLUTION_STATUS,
  WINDOW_MODE,
  clampExtentToSessionClose,
  createWindowPreference,
  extentFromEpochSeconds,
  inferBarIntervalSeconds,
  normalizeBarIntervalSeconds,
  resolveWindowPreference,
  sessionDomainSeconds,
} from '../../Utils/chartTimeWindow';
import { clampGexTimeRange } from '../../Utils/gexTimeWindow';
import { etDateKey } from '../../Utils/marketHours';

export default function useGexSharedTimeWindow({
  series,
  sourceId,
  setViewRange,
  viewDetachedRef,
}) {
  const sharedWindow = useChartTimeWindowStore((state) => state.preference);
  const sharedWindowSource = useChartTimeWindowStore((state) => state.source);
  const publishUserRange = useChartTimeWindowStore((state) => state.publishUserRange);
  const pendingPublishRef = useRef(null);
  const publishRafRef = useRef(null);
  const activeDomainRef = useRef(null);
  const observedWindowRef = useRef(undefined);
  const defaultFallbackActiveRef = useRef(false);

  const context = useMemo(() => {
    const times = series.map((point) => Math.floor(point.t / 1000));
    const barIntervalSeconds = inferBarIntervalSeconds(times);
    const observedExtent = extentFromEpochSeconds(times) || (
      times.length === 1
        ? { first: times[0] - barIntervalSeconds, last: times[0] }
        : null
    );
    const extent = clampExtentToSessionClose(observedExtent);
    if (!extent) return null;
    const session = sessionDomainSeconds(extent.last * 1000);
    const domainMs = { from: session.first * 1000, to: session.last * 1000 };
    return {
      extent,
      barIntervalSeconds,
      domainMs,
      minSpanMs: Math.min(
        normalizeBarIntervalSeconds(barIntervalSeconds) * MIN_VISIBLE_BARS * 1000,
        domainMs.to - domainMs.from,
      ),
    };
  }, [series]);

  const publishNow = useCallback((range, options) => {
    if (!context) return false;
    const published = publishUserRange(
      sourceId,
      { from: range.from / 1000, to: range.to / 1000 },
      context.extent,
      {
        barIntervalSeconds: context.barIntervalSeconds,
        tradingDate: etDateKey(context.extent.last * 1000),
        ...options,
      },
    );
    // A position publish clears local ownership after the store accepts it.
    // Replay stores the pinned position for its trading date, so later cutoff
    // updates keep the interval the viewer selected.
    if (options?.positionIsIntent && published) viewDetachedRef.current = false;
    return published;
  }, [context, publishUserRange, sourceId, viewDetachedRef]);

  const cancelPendingPublish = useCallback(() => {
    if (publishRafRef.current != null) {
      window.cancelAnimationFrame(publishRafRef.current);
      publishRafRef.current = null;
    }
    pendingPublishRef.current = null;
  }, []);

  const schedulePublish = useCallback((range, options) => {
    pendingPublishRef.current = { range, options };
    if (publishRafRef.current != null) return;
    publishRafRef.current = window.requestAnimationFrame(() => {
      publishRafRef.current = null;
      const pending = pendingPublishRef.current;
      pendingPublishRef.current = null;
      if (pending) publishNow(pending.range, pending.options);
    });
  }, [publishNow]);

  const publishSession = useCallback(() => {
    if (!context) return;
    cancelPendingPublish();
    viewDetachedRef.current = false;
    setViewRange({ start: context.domainMs.from, end: context.domainMs.to });
    publishNow(context.domainMs);
  }, [cancelPendingPublish, context, publishNow, setViewRange, viewDetachedRef]);

  useEffect(() => cancelPendingPublish, [cancelPendingPublish]);

  const applySharedWindow = useCallback((fallbackToDefaultLatest = false) => {
    if (!context) return false;

    const defaultWindow = createWindowPreference(
      {
        mode: WINDOW_MODE.LATEST,
        durationSeconds: DEFAULT_VISIBLE_SECONDS,
        isDefaultSpan: true,
      },
      { now: context.extent.last * 1000 },
    );
    let effectiveWindow = sharedWindow || defaultWindow;

    let { status, range } = resolveWindowPreference(
      effectiveWindow,
      context.extent,
      { barIntervalSeconds: context.barIntervalSeconds },
    );
    const sharedWindowUnavailable = Boolean(sharedWindow)
      && (status === RESOLUTION_STATUS.UNAVAILABLE || !range);
    if (
      sharedWindowUnavailable
      && (fallbackToDefaultLatest || defaultFallbackActiveRef.current)
    ) {
      effectiveWindow = defaultWindow;
      ({ status, range } = resolveWindowPreference(
        effectiveWindow,
        context.extent,
        { barIntervalSeconds: context.barIntervalSeconds },
      ));
    }
    if (status === RESOLUTION_STATUS.UNAVAILABLE || !range) return false;

    defaultFallbackActiveRef.current = sharedWindowUnavailable;

    const target = clampGexTimeRange(
      { from: range.from * 1000, to: range.to * 1000 },
      context.domainMs,
      context.minSpanMs,
      effectiveWindow.mode === WINDOW_MODE.LATEST ? 1 : 0.5,
    );
    if (!target) return false;
    viewDetachedRef.current = false;
    setViewRange({ start: target.from, end: target.to });
    return true;
  }, [context, setViewRange, sharedWindow, viewDetachedRef]);

  useEffect(() => {
    const domainKey = context?.domainMs.from ?? null;
    const sameDomain = activeDomainRef.current === domainKey;
    const sharedWindowChanged = observedWindowRef.current !== sharedWindow;
    activeDomainRef.current = domainKey;
    observedWindowRef.current = sharedWindow;
    if (!context) return;
    if (
      viewDetachedRef.current
      && sameDomain
      && (!sharedWindowChanged || sharedWindowSource === 'replay')
    ) return;

    applySharedWindow(!sameDomain);
  }, [applySharedWindow, context, sharedWindow, sharedWindowSource, viewDetachedRef]);

  return {
    domainMs: context?.domainMs ?? null,
    minSpanMs: context?.minSpanMs ?? 1,
    publishSession,
    schedulePublish,
  };
}
